import { RangePreset } from "../../newcomponents/datepicker";

// Simple UTC date formatters (no external library needed since page is always UTC)
function formatUTC(date: Date, format: string): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  switch (format) {
    case "HH:mm":
      return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
    case "MMM-dd":
      return `${months[date.getUTCMonth()]}-${pad(date.getUTCDate())}`;
    case "MMM 'yy":
      return `${months[date.getUTCMonth()]} '${date.getUTCFullYear().toString().slice(-2)}`;
    default:
      return date.toISOString();
  }
}

export type TimeStep = "hour" | "day" | "week" | "month";
export type PageView = "metric" | "cost";
export type GroupBy =
  | "account"
  | "org"
  | "gvc"
  | "location"
  | "chargeable_items"
  | "feature"
  // TODO update type to support any arbitrary value from features
  // rest is from chargeable items
  | "workloads"
  | "mk8s"
  | "volume-sets"
  | "custom-ingress"
  | "observability";

export interface ConsumptionQuery {
  id: string;
  filterBy: Record<string, string>;
  aggregateBy: string[];
  chargeableItems: string[];
}

export interface UsageRequestBody {
  detailed: boolean;
  startTime: string;
  endTime: string;
  aggregateByTimeStep: boolean;
  timeStep: TimeStep;
  groupBy: string[];
  mainConsumptionQuery: ConsumptionQuery;
  consumptionQueries: ConsumptionQuery[];
}

export function GroupByToLabel(groupBy: GroupBy): string {
  switch (groupBy) {
    case "account":
      return "Account";
    case "org":
      return "Org";
    case "gvc":
      return "GVC";
    case "location":
      return "Location";
    case "chargeable_items":
      return "Chargeable Items";
    case "feature":
      return "Feature";
    case "mk8s":
      return "Mk8s";
    case "workloads":
      return "Workload";
    case "volume-sets":
      return "Volume Set";
    case "custom-ingress":
      return "Custom Ingress";
    case "observability":
      return "Observability";
    default:
      return groupBy;
  }
}

export type NumberMap = { [_: string]: number };

export interface ChargeableItem {
  id: string;
  name: string;
  description: string;
  consumptionTags: { [_: string]: string };
  unit: string; // "cores * seconds"
  type: "consumption" | "surcharge"; // "cores * seconds"
}

export interface PeriodConsumption {
  total: number;
  projectedTotal?: number;
  value: number;
  projectedValue?: number;
  currency: "USD" | string;
  ratePlans: string[];
  tags: { [_: string]: string };
}

export interface PeriodGroup {
  key: { [_: string]: string };
  consumptions: PeriodConsumption[];
}

export interface Period {
  startTime: string;
  endTime: string;
  totalSeconds: number;
  elapsedSeconds?: number;
  consumptionCount: number;
  groups: PeriodGroup[];
}

export type OrderedArray = [number, string][];

export interface GraphData {
  periodTimes: string;

  primaryTotalMap: NumberMap;
  primaryTotalMapHidden: NumberMap;
  primaryTotalOthers: NumberMap;
  primaryTotalMapForDisplay: NumberMap;

  primaryTotalMapOrderedRaw: OrderedArray;

  secondaryTotalMap: NumberMap;
  secondaryTotalMapHidden: NumberMap;
  secondaryTotalOthers: NumberMap;
  secondaryTotalMapForDisplay: NumberMap;

  secondaryTotalMapOrderedRaw: OrderedArray;

  // secondary cost is ignored for the projected
  projectedTotalMap: NumberMap;
  projectedTotalMapHidden: NumberMap;
  projectedTotalOthers: NumberMap;
  projectedTotalMapForDisplay: NumberMap;

  valueMap: NumberMap;
  valueMapHidden: NumberMap;
  valueOthers: NumberMap;
  valueMapForDisplay: NumberMap;

  projectedValueMap: NumberMap;
  projectedValueMapHidden: NumberMap;
  projectedValueOthers: NumberMap;
  projectedValueMapForDisplay: NumberMap;

  valueMapOrderedRaw: OrderedArray;
}

export interface ChartUnit {
  dataKey: string;
  label: string;
  org?: string;
  gvc?: string;
  primaryTotalSum: number;
  secondaryTotalSum: number;
  projectedTotalSum: number;
  color: string;
}
export interface ChartData {
  units: ChartUnit[];
  graphData: GraphData[];
  metricUnit: string;
}

interface FormatXLabelProps {
  value: string;
  timeStep: TimeStep;
}

export function formatXLabel({ value, timeStep }: FormatXLabelProps): string {
  try {
    const startTimeStr = value.split(" ")[0];
    const endTimeStr = value.split(" ")[1];
    const startTime = new Date(startTimeStr);
    const endTime = new Date(endTimeStr);

    if (timeStep === "hour") {
      return formatUTC(startTime, "HH:mm");
    }
    if (timeStep === "day") {
      return formatUTC(startTime, "MMM-dd");
    }
    if (timeStep === "week") {
      return `${formatUTC(startTime, "MMM-dd")} - ${formatUTC(endTime, "MMM-dd")}`;
    }
    if (timeStep === "month") {
      return formatUTC(startTime, "MMM 'yy");
    }
    return value;
  } catch (e) {
    return value;
  }
}

export function formatXLabelSpreadsheet(periodTimes: string, timeStep: TimeStep): string {
  try {
    let startTimeIso = periodTimes.split(" ")[0];
    let endTimeIso = periodTimes.split(" ")[1];
    if (timeStep !== "hour") {
      startTimeIso = startTimeIso.split("T")[0];
      endTimeIso = endTimeIso.split("T")[0];
    }
    return `${startTimeIso} - ${endTimeIso}`;
  } catch (e) {
    return periodTimes;
  }
}

export function formatPrice(value: number) {
  return Number(value.toFixed(2));
}

export const featureLabelMap: { [_: string]: string } = {
  workloads: "Workloads",
  "custom-ingress": "Custom Ingress",
  "volume-sets": "Volume Sets",
  mk8s: "Mk8s",
  observability: "Observability",
};

export const chargeableItemLabelMap = {
  "ecbb3f5f-0b22-4c83-ba8e-1a36f899aeac": "Workloads - CPU",
  "6b611577-4bd8-44fa-9097-909a95242de1": "Workloads - Memory",
  "9f85f07d-e7cf-4931-a3c9-15cef006127a": "Workloads - Egress",
  "c9cf95aa-003f-4157-a214-5e5203f1fe0f": "Custom Ingress - Load Balancer",
  "3abd25b0-a886-43d8-808d-4e7499d4414f": "Volume Sets - Capacity - High Throughput SSD",
  "d7429d7a-e0dd-4334-a224-ade15c476f64": "Volume Sets - Capacity - General Purpose SSD",
  "8a049309-c074-4343-8e93-7e8dfde9d3cc": "Workloads - Replicas",
};

export function getUsageRequestBody(
  startTime: string,
  endTime: string,
  timeStep: TimeStep,
  pageView: PageView,
  groupBy: GroupBy,
  chargeableItems: ChargeableItem[],
  features: string[],
): UsageRequestBody {
  if (groupBy === "account") {
    groupBy = "org";
  }
  let groupByList: string[] = [];
  if (pageView === "metric") {
    groupByList.push("metric");
  }
  if (features.includes(groupBy)) {
    groupByList.push("name");
  } else {
    groupByList.push(groupBy);
  }

  // workloads and volume-sets features require also grouping by gvc
  if (["workloads", "volume-sets"].includes(groupBy)) {
    groupByList.push("gvc");
  }

  const cq: ConsumptionQuery = {
    id: "main",
    aggregateBy: [],
    filterBy: {},
    chargeableItems: [],
  };

  if (features.includes(groupBy)) {
    cq.aggregateBy.push("chargeable_items");
  } else {
    cq.aggregateBy.push(groupBy);
  }
  if (pageView === "metric") {
    cq.aggregateBy.push("metric");
  }
  if (["workloads", "volume-sets"].includes(groupBy)) {
    cq.aggregateBy.push("gvc");
  }
  for (const ci of chargeableItems) {
    cq.chargeableItems.push(ci.name);
  }

  return {
    detailed: false,
    startTime: startTime,
    endTime: endTime,
    aggregateByTimeStep: true,
    timeStep: timeStep,
    groupBy: groupByList,
    mainConsumptionQuery: cq,
    consumptionQueries: [],
  };
}

// =============================================================================
// UTC Date Helper Functions for Usage Presets
// =============================================================================

function startOfHourUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCMinutes(0, 0, 0);
  return d;
}

export function startOfDayUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function startOfMonthUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function startOfYearUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCMonth(0, 1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function startOfWeekUTC(date: Date): Date {
  const d = new Date(date);
  const day = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - day);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function addHoursUTC(date: Date, hours: number): Date {
  return new Date(date.getTime() + hours * 60 * 60 * 1000);
}

function addDaysUTC(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function addWeeksUTC(date: Date, weeks: number): Date {
  return addDaysUTC(date, weeks * 7);
}

function addMonthsUTC(date: Date, months: number): Date {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}

function addYearsUTC(date: Date, years: number): Date {
  const d = new Date(date);
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d;
}

// =============================================================================
// New Preset Functions (using native Date with UTC)
// =============================================================================

export function getUsageHourPresetsNew(): RangePreset[] {
  const now = new Date();
  const nextHourStart = startOfHourUTC(addHoursUTC(now, 1));
  const todayStart = startOfDayUTC(now);

  return [
    { label: "Last 3 Hours", value: [addHoursUTC(nextHourStart, -3).toISOString(), nextHourStart.toISOString()] },
    { label: "Last 6 Hours", value: [addHoursUTC(nextHourStart, -6).toISOString(), nextHourStart.toISOString()] },
    { label: "Last 12 Hours", value: [addHoursUTC(nextHourStart, -12).toISOString(), nextHourStart.toISOString()] },
    { label: "Last 24 Hours", value: [addHoursUTC(nextHourStart, -24).toISOString(), nextHourStart.toISOString()] },
    { label: "Today", value: [todayStart.toISOString(), addDaysUTC(todayStart, 1).toISOString()] },
    { label: "Yesterday", value: [addDaysUTC(todayStart, -1).toISOString(), todayStart.toISOString()] },
  ];
}

export function getUsageDayPresetNew(): RangePreset[] {
  const now = new Date();
  const todayStart = startOfDayUTC(now);
  const thisMonthStart = startOfMonthUTC(now);

  return [
    { label: "Last 7 Days", value: [addDaysUTC(todayStart, -6).toISOString(), todayStart.toISOString()] },
    { label: "Last 15 Days", value: [addDaysUTC(todayStart, -14).toISOString(), todayStart.toISOString()] },
    { label: "Last 30 Days", value: [addDaysUTC(todayStart, -29).toISOString(), todayStart.toISOString()] },
    { label: "This Month", value: [thisMonthStart.toISOString(), todayStart.toISOString()] },
    { label: "Last Month", value: [addMonthsUTC(thisMonthStart, -1).toISOString(), addDaysUTC(thisMonthStart, -1).toISOString()] },
  ];
}

export function getUsageWeekPresetNew(): RangePreset[] {
  const now = new Date();
  const todayStart = startOfDayUTC(now);
  const weekStart = addDaysUTC(startOfWeekUTC(now), 1); // Monday
  const thisMonthStart = startOfMonthUTC(now);

  return [
    {
      label: "Last 6 Weeks",
      value: [addWeeksUTC(weekStart, -5).toISOString(), todayStart.toISOString()],
    },
    { label: "This Month", value: [thisMonthStart.toISOString(), todayStart.toISOString()] },
    { label: "Last Month", value: [addMonthsUTC(thisMonthStart, -1).toISOString(), addDaysUTC(thisMonthStart, -1).toISOString()] },
  ];
}

export function getUsageMonthPresetNew(): RangePreset[] {
  const now = new Date();
  const todayStart = startOfDayUTC(now);
  const thisMonthStart = startOfMonthUTC(now);
  const thisYearStart = startOfYearUTC(now);

  return [
    { label: "Last 3 Months", value: [addMonthsUTC(thisMonthStart, -2).toISOString(), todayStart.toISOString()] },
    { label: "Last 6 Months", value: [addMonthsUTC(thisMonthStart, -5).toISOString(), todayStart.toISOString()] },
    { label: "Last 12 Months", value: [addMonthsUTC(thisMonthStart, -11).toISOString(), todayStart.toISOString()] },
    { label: "Last 24 Months", value: [addMonthsUTC(thisMonthStart, -23).toISOString(), todayStart.toISOString()] },
    { label: "This Year", value: [thisYearStart.toISOString(), todayStart.toISOString()] },
    { label: "Last Year", value: [addYearsUTC(thisYearStart, -1).toISOString(), addDaysUTC(thisYearStart, -1).toISOString()] },
  ];
}

export type IEC_UNIT = "B" | "KiB" | "MiB" | "GiB" | "TiB";
export type DECIMAL_UNIT = "B" | "KB" | "MB" | "GB" | "TB";

const IEC_MULTIPLIERS: Record<IEC_UNIT, number> = {
  B: 1,
  KiB: 1024,
  MiB: 1024 ** 2,
  GiB: 1024 ** 3,
  TiB: 1024 ** 4,
};

export const IEC_UNITS = Object.keys(IEC_MULTIPLIERS) as IEC_UNIT[];

const DECIMAL_MULTIPLIERS: Record<DECIMAL_UNIT, number> = {
  B: 1,
  KB: 1000,
  MB: 1000 ** 2,
  GB: 1000 ** 3,
  TB: 1000 ** 4,
};

export const DECIMAL_UNITS = Object.keys(DECIMAL_MULTIPLIERS) as DECIMAL_UNIT[];

export const IEC_Helpers = {
  convertInputToBytes(value: number, unit: IEC_UNIT) {
    const multiplier = IEC_MULTIPLIERS[unit];
    return value * multiplier;
  },
  convertInputArrayToBytes(values: number[], unit: IEC_UNIT) {
    return values.map((value) => this.convertInputToBytes(value, unit));
  },
  getBestDisplayUnit(bytesList: number[]): IEC_UNIT {
    const max = Math.max(...bytesList);
    let exponent = Math.floor(Math.log(max) / Math.log(1024));
    exponent = Math.min(exponent, 4); // Clamp to TiB max
    return ["B", "KiB", "MiB", "GiB", "TiB"][exponent] as IEC_UNIT;
  },
  convertBytesToUnit(bytes: number, targetUnit: IEC_UNIT) {
    const multiplier = IEC_MULTIPLIERS[targetUnit];
    return bytes / multiplier;
  },
  convertBytesArrayToUnit(bytesList: number[], targetUnit: IEC_UNIT) {
    return bytesList.map((input) => this.convertBytesToUnit(input, targetUnit));
  },
};

export const DECIMAL_Helpers = {
  convertInputToBytes(value: number, unit: DECIMAL_UNIT) {
    const multiplier = DECIMAL_MULTIPLIERS[unit];
    return value * multiplier;
  },
  convertInputArrayToBytes(values: number[], unit: DECIMAL_UNIT) {
    return values.map((value) => this.convertInputToBytes(value, unit));
  },
  getBestDisplayUnit(bytesList: number[]): DECIMAL_UNIT {
    const max = Math.max(...bytesList);
    let exponent = Math.floor(Math.log(max) / Math.log(1000));
    exponent = Math.min(exponent, 4); // Clamp to TB max
    return ["B", "KB", "MB", "GB", "TB"][exponent] as DECIMAL_UNIT;
  },
  convertBytesToUnit(bytes: number, targetUnit: DECIMAL_UNIT) {
    const multiplier = DECIMAL_MULTIPLIERS[targetUnit];
    return bytes / multiplier;
  },
  convertBytesArrayToUnit(bytesList: number[], targetUnit: DECIMAL_UNIT) {
    return bytesList.map((input) => this.convertBytesToUnit(input, targetUnit));
  },
};

// =============================================================================
// Time-Based Metric Helpers (for "X * Seconds" units)
// =============================================================================

// Detect if a unit contains "* Seconds" pattern
export function isTimeBasedUnit(unit: string): boolean {
  return unit.includes("* Seconds");
}

// Extract the base unit (e.g., "Cores * Seconds" → "Cores")
export function getBaseUnit(unit: string): string {
  return unit.replace(" * Seconds", "").trim();
}

// Get display label for averaged metric
export function getAveragedUnitLabel(unit: string): string {
  const baseUnit = getBaseUnit(unit);
  // Map to user-friendly labels
  const labelMap: Record<string, string> = {
    Cores: "Avg Cores",
    GB: "Avg GB",
    Bytes: "Avg Bytes",
    Replicas: "Avg Replicas",
    "Load Balancers": "Avg Load Balancers",
    Snapshots: "Avg Snapshots",
  };
  return labelMap[baseUnit] || `Avg ${baseUnit}`;
}

// Convert cumulative value to average per timestep (deprecated - kept for reference)
export function convertToAveragePerTimestep(value: number, totalSeconds: number): number {
  if (totalSeconds <= 0) return value;
  return value / totalSeconds;
}

// Get seconds per timestep
export function getSecondsPerTimestep(timeStep: TimeStep): number {
  switch (timeStep) {
    case "hour":
      return 3600;
    case "day":
      return 86400;
    case "week":
      return 604800;
    case "month":
      return 2592000; // 30 days
    default:
      return 86400;
  }
}

// Get display label for timestep
export function getTimestepLabel(timeStep: TimeStep): string {
  switch (timeStep) {
    case "hour":
      return "Hour";
    case "day":
      return "Day";
    case "week":
      return "Week";
    case "month":
      return "Month";
    default:
      return "Day";
  }
}

// Convert value from "X * Seconds" to "X * Timestep"
export function convertSecondsToTimestep(value: number, secondsPerTimestep: number): number {
  return value / secondsPerTimestep;
}

// Build the cumulative unit label (e.g., "Bytes * Day", "Cores * Hour")
export function getCumulativeUnitLabel(baseUnit: string, timeStep: TimeStep): string {
  return `${baseUnit} * ${getTimestepLabel(timeStep)}`;
}
